import { SESSION_MAX_SECONDS } from '../constants/config';
import { refreshAccessToken, getUser } from './auth0';
import { writeSession } from '../actions/authThunks';

// Replaces the stored access token before it expires, so the synchronous
// getToken() in actionCreators.ts always finds a live one. Renewing here rather
// than at the point of use is what lets those 28 thunks stay unchanged.
//
// Lives outside React deliberately: started once from the bootstrap, so
// StrictMode's double mount cannot start two of them.

// Refresh at ~75% of the clamped lifetime, leaving room for a retry-by-reload.
const MARGIN_MS = SESSION_MAX_SECONDS * 0.25 * 1000;

let timer: number | null = null;
// The expiry this tab's timer was set against. If storage no longer matches when
// the timer fires, another tab has already refreshed.
let scheduledFor: number | null = null;

const clear = () => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
};

/** Stored expiry, or null when there is no usable session. */
const storedExpiry = (): number | null => {
  const raw = localStorage.getItem('auth0');
  if (!raw) return null;
  try {
    const { expiresAt } = JSON.parse(raw);
    return typeof expiresAt === 'number' ? expiresAt : null;
  } catch {
    return null;
  }
};

const schedule = () => {
  clear();
  const expiresAt = storedExpiry();
  if (expiresAt === null) return;
  scheduledFor = expiresAt;
  timer = window.setTimeout(refreshNow, Math.max(expiresAt - Date.now() - MARGIN_MS, 0));
};

// Serialised across tabs: with rotation, two tabs refreshing at once means one
// presents a refresh token the other just spent. Auth0 treats that as reuse and
// revokes the whole token family — `invalid_grant` in every tab (seen in testing).
async function refreshNow() {
  if (navigator.locks) {
    await navigator.locks.request('taplause-token-refresh', refreshIfStillDue);
  } else {
    await refreshIfStillDue();
  }
}

async function refreshIfStillDue() {
  const expiresAt = storedExpiry();
  // Storage empty means a logout happened meanwhile — stop rather than reviving
  // the session. (logout() cannot call in here: that import would be circular.)
  if (expiresAt === null) {
    clear();
    return;
  }
  // Another tab refreshed while this one waited for the lock: use its token.
  if (expiresAt !== scheduledFor) {
    schedule();
    return;
  }

  try {
    const { access_token, expires_in } = await refreshAccessToken();
    const profile = await getUser();
    if (!profile) throw new Error('Auth0 returned no user profile');

    writeSession(access_token, expires_in, profile);
    schedule();
  } catch (error) {
    // Leave the stored token alone — it may still be valid, and if it is not,
    // tokenExpired() handles it on the next request. Retrying here would loop.
    console.error('*** Token refresh failed:%o', error);
    clear();
  }
}

// Background tabs have their timers throttled, so one can wake up past its
// refresh point. Re-check on the way back instead of trusting the timer.
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && timer !== null) schedule();
};

/** Starts refreshing. No-op when logged out. Safe to call more than once. */
export const startTokenRefresh = () => {
  if (storedExpiry() === null) return;
  schedule();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  document.addEventListener('visibilitychange', onVisibilityChange);
};

export const stopTokenRefresh = () => {
  clear();
  document.removeEventListener('visibilitychange', onVisibilityChange);
};
